import {z} from 'zod';
import {COLUMN_LIMITED_CONDITIONS} from './constants';

export const ColumnListItemSchema = z.object({
  id: z.string(),
  thumbnailURL: z.string().optional(),
  carouselThumbnailURL: z.string().optional(), // DEPRECATED
  cardImageURL: z.string().optional(),
  category: z.string(),
  subCategory: z.string(),
  tags: z.string().array(),
  publishDateTime: z.string(), // ISO 8601形式の文字列
  updateDateTime: z.string(), // ISO 8601形式の文字列
  title: z.string(),
  contentSummary: z.string(),
  favorited: z.boolean(),
  favoriteCount: z.number(),
  special: z
    .object({
      slug: z.string(),
    })
    .optional(),
  companyPages: z
    .array(
      z.object({
        companyID: z.number(),
        slug: z.string(),
      }),
    )
    .optional(),
  sponsorCompanies: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
      }),
    )
    .optional(),
});
export type ColumnListItemType = z.infer<typeof ColumnListItemSchema>;

export const ColumnPostListSchema = z.object({
  posts: z.array(ColumnListItemSchema),
});

const ColumnLimitedCondition = z.enum([COLUMN_LIMITED_CONDITIONS.LOGIN]);

// GET /3.0/column/posts/{id}
export const PostSchema = z.object({
  id: z.string(),
  thumbnailURL: z.string().optional(),
  ogpImageURL: z.string().optional(),
  headerURL: z.string().optional(),
  category: z.string(),
  categoryJP: z.string().optional(),
  subCategory: z.string(),
  subCategoryJP: z.string().optional(),
  tags: z.array(z.string()),
  publishDateTime: z.string(), // ISO 8601形式の文字列
  updateDateTime: z.string(), // ISO 8601形式の文字列
  title: z.string(),
  description: z.string().optional(),
  content: z.string(),
  contentHTML: z.string().optional(),
  favorited: z.boolean(),
  favoriteCount: z.number(),
  companyPages: z
    .array(
      z.object({
        companyID: z.number(),
        slug: z.string(),
      }),
    )
    .optional(),
  sponsorCompanies: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
      }),
    )
    .optional(),
  limitedConditions: z.array(ColumnLimitedCondition).nullable(),
  redirectTo: z.string(),
  special: z
    .object({
      Slug: z.string(),
    })
    .nullable(),
  industryIDs: z.array(z.number()).optional(),
  industryCategoryIDs: z.array(z.number()).optional(),
});
export const PostListSchema = z.object({
  id: z.string(),
  eyecatchURL: z.string().optional(),
  thumbnailURL: z.string().optional(),
  carouselThumbnailURL: z.string().optional(),
  category: z.string(),
  subCategory: z.string(),
  tags: z.array(z.string()),
  publishDateTime: z.string(),
  updateDateTime: z.string(),
  title: z.string(),
  contentSummary: z.string(),
  favorited: z.boolean(),
  favoriteCount: z.number(),
});

export type PostSchema = z.infer<typeof PostSchema>;
export type PostListSchema = z.infer<typeof PostListSchema>;

export const GetPostResponse = z.object({
  post: PostSchema,
});
export type GetPostResponse = z.infer<typeof GetPostResponse>;
export const GetPostListResponse = z.object({
  posts: z.array(PostListSchema),
});
export type GetPostListResponse = z.infer<typeof GetPostListResponse>;

export const SpecialPostSchema = z.object({
  id: z.string(),
  title: z.string(),
});

export const SpecialPostListSchema = z.object({
  posts: z.array(SpecialPostSchema),
});

export const ColumnCategorySchema = z.object({
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  count: z.number(),
});

export type ColumnCategory = z.infer<typeof ColumnCategorySchema>;

export const ColumnCategoriesSchema = z.object({
  categories: z.array(
    z.object({
      category: ColumnCategorySchema,
      children: z.array(
        z.object({
          category: ColumnCategorySchema,
        }),
      ),
    }),
  ),
});

export const ColumnCompanySchema = z
  .object({
    companyID: z.number(),
    companyName: z.string(),
    logoURL: z.string().optional(),
    mainPhotoURL: z.string().optional(),
    industryID: z.number().optional(),
    industryName: z.string().optional(),
    columnCount: z.number(),
    nativeAdsCount: z.number(),
    followers: z.number(),
    isFollowed: z.boolean(),
    isContracted: z.boolean(),
  })
  .refine((data) => {
    // industryIDとindustryNameはセット
    if (data.industryID) {
      return data.industryName !== undefined;
    }
    return true;
  });
export type ColumnCompany = z.infer<typeof ColumnCompanySchema>;

export const ColumnTagSchema = z.object({
  name: z.string(),
  description: z.string(),
});
export type ColumnTag = z.infer<typeof ColumnTagSchema>;

export const RankingColumnListSchema = z.object({
  posts: z.array(ColumnListItemSchema),
});

export const ColumnCarouselSchema = z.object({
  id: z.string(),
  cardImageURL: z.string().optional(),
  imageURL: z.string(),
  title: z.string(),
  category: z.string(),
  subCategory: z.string(),
  favoriteCount: z.number(),
  favorited: z.boolean(),
  tags: z.string().array(),
});

export const GetColumnCarouselList = z.object({
  carousel: z.array(ColumnCarouselSchema),
});

export type ColumnCarousel = z.infer<typeof ColumnCarouselSchema>;

export const GetPreviewCodeResponseSchema = z.object({
  previewCode: z.string(),
});
export type GetPreviewCodeResponse = z.infer<
  typeof GetPreviewCodeResponseSchema
>;
